import { useCallback } from 'react'

import { useAuthInfo } from '@sweetauth/core'

import type { PromptFilterToggles } from '@/components/library-v2/prompts/PromptFilterTabs'

import { usePrompts } from '@/hooks/prompts/usePrompts'

import type { UUID } from '@/types/Id'
import type { SavedPrompt } from '@/types/library/SavedPrompt'

export interface GroupedPrompts {
  myPrompts: SavedPrompt[]
  orgPrompts: SavedPrompt[]
}

export const byUsage = (a: SavedPrompt, b: SavedPrompt): number =>
  b.usage_count - a.usage_count

const createGroupedPromptsSelector =
  (userId: string | undefined) =>
  (prompts: SavedPrompt[]): GroupedPrompts => {
    const my: SavedPrompt[] = []
    const org: SavedPrompt[] = []
    for (const p of prompts) {
      if (p.archived) continue
      if (p.user_id === userId) {
        my.push(p)
      }
      if (p.has_unrestricted_access) {
        org.push(p)
      }
    }
    return { myPrompts: my.sort(byUsage), orgPrompts: org.sort(byUsage) }
  }

interface UseGroupedPromptsArgs {
  orgId: UUID | ''
}

/**
 * Returns grouped prompts (my prompts vs org prompts) using a selector.
 * Returns the full query object for access to isLoading, error, etc.
 */
export const useGroupedPrompts = ({ orgId }: UseGroupedPromptsArgs) => {
  const { user } = useAuthInfo()

  const selectGroupedPrompts = useCallback(
    (prompts: SavedPrompt[]) =>
      createGroupedPromptsSelector(user?.userId)(prompts),
    [user?.userId],
  )

  // Return the query directly - consumers get full query access
  return usePrompts({
    orgId,
    select: selectGroupedPrompts,
  })
}

// Types for the filtered prompts result
export interface FilteredPromptsData {
  myPrompts: SavedPrompt[]
  orgPrompts: SavedPrompt[]
  allPrompts: SavedPrompt[]
  hasPrompts: boolean
  hasResults: boolean
}

interface UseFilteredPromptsArgs {
  orgId: UUID | ''
  filters: PromptFilterToggles
  searchQuery?: string
}

/**
 * Creates a selector that filters and groups prompts based on search and filter criteria.
 * All filtering logic is done in the selector for optimal memoization.
 */
const createFilteredPromptsSelector =
  (
    userId: string | undefined,
    filters: PromptFilterToggles,
    searchQuery: string,
  ) =>
  (prompts: SavedPrompt[]): FilteredPromptsData => {
    // First, group prompts (respecting archived filter)
    const my: SavedPrompt[] = []
    const org: SavedPrompt[] = []
    for (const p of prompts) {
      // Skip archived prompts unless showArchived is enabled
      if (p.archived && !filters.showArchived) continue
      if (p.user_id === userId) {
        my.push(p)
      }
      if (p.has_unrestricted_access) {
        org.push(p)
      }
    }

    // Sort by usage
    my.sort(byUsage)
    org.sort(byUsage)

    // Calculate total prompts count (deduplicated)
    const totalPrompts = new Map([...my, ...org].map((p) => [p.id, p])).size

    // Apply search filter
    const query = searchQuery.toLowerCase()
    const searchFn = (p: SavedPrompt): boolean =>
      !searchQuery ||
      p.name.toLowerCase().includes(query) ||
      p.content.toLowerCase().includes(query)

    const filteredMy = filters.showMine ? my.filter(searchFn) : []
    const filteredOrg = filters.showOrg ? org.filter(searchFn) : []

    // Deduplicate and sort all prompts for display
    const allPrompts = Array.from(
      new Map([...filteredMy, ...filteredOrg].map((p) => [p.id, p])).values(),
    ).sort(byUsage)

    return {
      myPrompts: filteredMy,
      orgPrompts: filteredOrg,
      allPrompts,
      hasPrompts: totalPrompts > 0,
      hasResults: allPrompts.length > 0,
    }
  }

/**
 * Returns filtered prompts using a selector pattern.
 * All filtering is done in the selector for optimal memoization.
 * Returns the full query object for access to isLoading, error, etc.
 */
export const useFilteredPrompts = ({
  orgId,
  filters,
  searchQuery = '',
}: UseFilteredPromptsArgs) => {
  const { user } = useAuthInfo()

  const selectFilteredPrompts = useCallback(
    (prompts: SavedPrompt[]) =>
      createFilteredPromptsSelector(
        user?.userId,
        filters,
        searchQuery,
      )(prompts),
    [user?.userId, filters, searchQuery],
  )

  // Return the query directly - consumers get full query access
  return usePrompts({
    orgId,
    select: selectFilteredPrompts,
  })
}
